import React, { useState, useCallback, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
} from "@mui/material";
import Cropper, { Area } from "react-easy-crop";
import { NoticeboardButton } from "../shared";
import { lightTheme } from "../../styles/theme";

interface ImageCropperModalProps {
  open: boolean;
  imageSrc: string;
  onClose: () => void;
  onCropComplete: (croppedImageUrl: string) => void | Promise<void>;
  aspectRatio?: number;
  onChangePhoto?: () => void;
}

const ImageCropperModal: React.FC<ImageCropperModalProps> = ({
  open,
  imageSrc,
  onClose,
  onCropComplete,
  onChangePhoto,
  aspectRatio = 4 / 3, // Default 4:3 aspect ratio for listings
}) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [processedImageSrc, setProcessedImageSrc] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const onCropChange = useCallback((crop: { x: number; y: number }) => {
    setCrop(crop);
  }, []);

  const onZoomChange = useCallback((zoom: number) => {
    setZoom(zoom);
  }, []);

  const onCropCompleteCallback = useCallback(
    (croppedArea: Area, croppedAreaPixels: Area) => {
      setCroppedAreaPixels(croppedAreaPixels);
    },
    []
  );

  // Convert URL to data URL to handle CORS issues
  const urlToDataUrl = async (url: string): Promise<string> => {
    // If it's already a data URL, return it
    if (url.startsWith("data:")) {
      return url;
    }

    try {
      // Fetch the image as a blob
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to fetch image: ${response.statusText}`);
      }
      const blob = await response.blob();
      
      // Convert blob to data URL
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.addEventListener("load", () => resolve(reader.result as string));
        reader.addEventListener("error", (error) => reject(error));
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error("Error converting URL to data URL:", error);
      throw new Error("Failed to load image. Please check if the image URL is accessible.");
    }
  };

  const createImage = (url: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.addEventListener("load", () => resolve(image));
      image.addEventListener("error", (error) => reject(error));
      image.src = url;
    });

  // Process image source when it changes
  useEffect(() => {
    if (!open || !imageSrc) {
      setProcessedImageSrc("");
      setError(null);
      setSaveError(null);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
      return;
    }

    const processImage = async () => {
      setIsLoading(true);
      setError(null);
      try {
        // Convert URL to data URL if needed (handles CORS)
        const dataUrl = await urlToDataUrl(imageSrc);
        setProcessedImageSrc(dataUrl);
      } catch (err) {
        console.error("Error processing image:", err);
        setError(err instanceof Error ? err.message : "Failed to load image");
      } finally {
        setIsLoading(false);
      }
    };

    processImage();
  }, [open, imageSrc]);

  const getCroppedImg = async (
    imageSrc: string,
    pixelCrop: Area
  ): Promise<string> => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    if (!ctx) {
      throw new Error("No 2d context");
    }

    // Calculate dimensions that maintain the exact aspect ratio
    // This ensures the cropped image matches the mobile app display exactly
    let outputWidth = pixelCrop.width;
    let outputHeight = pixelCrop.height;
    
    // If aspectRatio is provided, enforce it exactly
    if (aspectRatio) {
      const currentRatio = pixelCrop.width / pixelCrop.height;
      if (Math.abs(currentRatio - aspectRatio) > 0.01) {
        // Adjust to match exact aspect ratio
        if (currentRatio > aspectRatio) {
          // Too wide, adjust width
          outputWidth = pixelCrop.height * aspectRatio;
        } else {
          // Too tall, adjust height
          outputHeight = pixelCrop.width / aspectRatio;
        }
      }
    }

    canvas.width = outputWidth;
    canvas.height = outputHeight;

    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      outputWidth,
      outputHeight
    );

    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error("Canvas is empty"));
            return;
          }
          const reader = new FileReader();
          reader.addEventListener("load", () => resolve(reader.result as string));
          reader.addEventListener("error", (error) => reject(error));
          reader.readAsDataURL(blob);
        },
        "image/jpeg",
        0.9 // Quality: 90%
      );
    });
  };

  const handleSave = async () => {
    if (isSaving) return;
    if (!croppedAreaPixels || !processedImageSrc) {
      console.warn("Cannot save: missing croppedAreaPixels or processedImageSrc", {
        croppedAreaPixels,
        processedImageSrc: !!processedImageSrc,
      });
      return;
    }

    try {
      setIsSaving(true);
      setSaveError(null);
      const croppedImageUrl = await getCroppedImg(processedImageSrc, croppedAreaPixels);
      
      // Verify the cropped image URL is valid
      if (!croppedImageUrl || typeof croppedImageUrl !== "string") {
        throw new Error("Invalid cropped image URL");
      }
      
      await onCropComplete(croppedImageUrl);
      onClose();
    } catch (error) {
      console.error("Error cropping image:", error);
      setSaveError(error instanceof Error ? error.message : "Failed to save image. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (!isSaving) onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: "90vh",
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        {/* Fixed height on the photo alone, so the zoom slider sits below it, not under it. */}
        <Box sx={{ position: "relative", height: { xs: 320, sm: 400 } }}>
        {isLoading ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              bgcolor: lightTheme.colors.pureBlack,
            }}
          >
            <Typography variant="body1" sx={{ color: "common.white" }}>
              Loading image...
            </Typography>
          </Box>
        ) : error ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              bgcolor: lightTheme.colors.pureBlack,
              p: 3,
            }}
          >
            <Typography variant="body1" sx={{ color: "error.main", mb: 2, textAlign: "center" }}>
              {error}
            </Typography>
            <Button onClick={onClose} variant="outlined" sx={{ color: "common.white", borderColor: "white" }}>
              Close
            </Button>
          </Box>
        ) : processedImageSrc ? (
          <Box
            sx={{
              position: "relative",
              width: "100%",
              height: "100%",
              bgcolor: lightTheme.colors.pureBlack,
            }}
          >
            <Cropper
              image={processedImageSrc}
              crop={crop}
              zoom={zoom}
              aspect={aspectRatio}
              onCropChange={onCropChange}
              onZoomChange={onZoomChange}
              onCropComplete={onCropCompleteCallback}
              style={{
                containerStyle: {
                  width: "100%",
                  height: "100%",
                },
              }}
            />
          </Box>
        ) : null}
        </Box>
        {/* Zoom Controls */}
        <Box sx={{ p: 2, bgcolor: "background.paper", borderTop: 1, borderColor: "divider" }}>
          <Typography variant="body2" sx={{ mb: 1, color: "text.secondary" }}>
            Zoom
          </Typography>
          <input
            type="range"
            value={zoom}
            min={1}
            max={3}
            step={0.1}
            onChange={(e) => setZoom(Number(e.target.value))}
            style={{ width: "100%" }}
          />
        </Box>
      </DialogContent>
      {saveError && (
        <Typography role="alert" color="error" sx={{ px: 2, pt: 2 }}>
          {saveError}
        </Typography>
      )}
      {/* Phones: Change photo gets its own row so three buttons never squeeze. */}
      <DialogActions
        disableSpacing
        sx={{ p: 2, gap: 1, flexWrap: "wrap", "& > *": { flex: { xs: 1, sm: "none" } } }}
      >
        {onChangePhoto && (
          <NoticeboardButton
            variant="secondary"
            onClick={onChangePhoto}
            disabled={isSaving}
            sx={{ flexBasis: { xs: "100%", sm: "auto" }, mr: { sm: "auto" } }}
          >
            Change photo
          </NoticeboardButton>
        )}
        <NoticeboardButton variant="secondary" onClick={handleCancel} disabled={isSaving}>
          Cancel
        </NoticeboardButton>
        <NoticeboardButton
          variant="primary"
          onClick={handleSave}
          disabled={isLoading || isSaving || !!error || !processedImageSrc || !croppedAreaPixels}
        >
          {isSaving ? "Saving image..." : "Save"}
        </NoticeboardButton>
      </DialogActions>
    </Dialog>
  );
};

export default ImageCropperModal;
